
import { toast } from '@/components/ui/sonner';
import { auth, supabase } from '@/lib/supabase';
import { User } from '@supabase/supabase-js';
import { UserType } from '@/data/campaign-types';
import { UserDetails } from '@/contexts/AuthContext';
import { logError } from '@/lib/sentry';
import { buildSignupMetadata } from '@/lib/signup-metadata';

/**
 * Hook with authentication methods
 */
export function useAuthMethods(user: User | null, setUserDetails: React.Dispatch<React.SetStateAction<UserDetails | null>>) {
  const signUp = async (email: string, password: string, userType: UserType, userData: any) => {
    try {

      const userMetadata = buildSignupMetadata(userType, userData);

      const { data, error } = await auth.signUp(email, password, userMetadata);

      if (error) {
        logError("Sign up failed", error);
        toast.error(error.message || "Failed to create account");
        throw error;
      }

      const requiresEmailConfirmation = Boolean(data.user && !data.session);

      // Create user profile immediately after signup when we have a session
      if (data.user && data.session) {
        console.log("Creating user profile for new signup:", data.user.id);
        const { success, message, error: profileError } = await auth.ensureUserProfile();
        if (!success) {
          logError("Failed to create user profile after signup", profileError || message);
          // Don't throw - allow signup to complete even if profile creation fails
          // The profile will be created on first sign-in
        }
      }

      return { requiresEmailConfirmation };
    } catch (error: any) {
      logError("Sign up error in catch block", error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      
      const { data, error } = await auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const message = error.message || "Failed to sign in";
        if (message.toLowerCase().includes("email not confirmed")) {
          toast.error("Please confirm your email before signing in.");
          const suppressed = new Error(message);
          (suppressed as { suppressSentry?: boolean }).suppressSentry = true;
          throw suppressed;
        }

        logError("Sign in failed", error);
        toast.error(message);
        throw error;
      }
      
      if (data.user) {
        // Block banned accounts before establishing a session
        const { data: userRow, error: userLookupError } = await supabase
          .from('users')
          .select('is_banned')
          .eq('id', data.user.id)
          .maybeSingle();

        if (userLookupError) {
          await auth.signOut();
          const err = new Error(
            'Unable to verify account access right now. Please try again.'
          );
          (err as { suppressSentry?: boolean }).suppressSentry = true;
          toast.error(err.message);
          throw err;
        }

        if (userRow?.is_banned) {
          await auth.signOut();
          const err = new Error(
            'Your account has been suspended. If you believe this is an error, please contact support@audiotrack.app.'
          );
          (err as { suppressSentry?: boolean }).suppressSentry = true;
          toast.error(err.message);
          throw err;
        }

        const { success } = await auth.ensureUserProfile();
      }
    } catch (error: any) {
      logError("Sign in error in catch block", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Prevent mock preview context from leaking across sessions on shared devices.
      if (typeof window !== "undefined") {
        localStorage.removeItem("admin-mock-creator-id");
        localStorage.removeItem("admin-mock-marketer-id");
      }

      const { error } = await auth.signOut();
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || "Failed to sign out");
      throw error;
    }
  };

  const updateUserDetails = async (details: Partial<UserDetails>) => {
    try {
      if (!user) throw new Error("User not authenticated");
      
      const { error } = await supabase
        .from('users')
        .update(details)
        .eq('id', user.id);

      if (error) throw error;

      setUserDetails((prev) => prev ? { ...prev, ...details } : null);
    } catch (error: any) {
      toast.error(error.message || "Failed to update profile");
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      
      const { data, error } = await auth.resetPassword(email);

      if (error) {
        logError("Password reset failed", error);
        toast.error(error.message || "Failed to send reset email");
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      logError("Password reset error in catch block", error);
      throw error;
    }
  };

  return {
    signIn,
    signUp,
    signOut,
    updateUserDetails,
    resetPassword
  };
}
